import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import SpiderChartSmall from "./SpiderChartSmall";
import Switch from "@mui/material/Switch";
import "./DuplicateData.scss";
import { percentageCalculation } from "../../../components/common/utilities";
import { formatCount } from "../DataTagConstants";
import Tooltip from "@mui/material/Tooltip";

function TypesRadarWithLegend({ data, total }) {
  // Ensure descending order
  const sortedData = [...data].sort((a, b) => b.total - a.total);

  const column1 = sortedData.slice(0, 5);
  const column2 = sortedData.slice(5, 10);

  const getPercentage = (count) => {
    if (!total || total === 0) return 0;

    return percentageCalculation([{ count }], total)[0]?.unRoundPercentage || 0;
  };

  return (
    <div className="types-radar-layout">
      <div className="types-radar-left">
        <SpiderChartSmall
          loc="TypesData"
          data={sortedData.map((d) => ({
            ...d,
            duplicate: 0,
          }))}
        />
      </div>

      {/* Right: Legend */}
      <div className="types-radar-legend two-columns">
        {/* Column 1 */}
        <div className="legend-column">
          {column1.map((item) => {
            const percentage = getPercentage(item.total);

            return (
              <div
                key={item.type}
                className="types-legend-row"
                title={`${item.type}: ${item.total} (${percentage}%)`}
              >
                <span className="types-dot" />
                <span className="types-name">{item.type}</span>
                <span className="types-count">
                  {formatCount(item.total)}
                  <span className="types-percent"> ({percentage}%)</span>
                </span>
              </div>
            );
          })}
        </div>

        {/* Column 2 */}
        <div className="legend-column">
          {column2.map((item) => {
            const percentage = getPercentage(item.total);

            return (
              <div
                key={item.type}
                className="types-legend-row"
                title={`${item.type}: ${item.total} (${percentage}%)`}
              >
                <span className="types-dot" />
                <span className="types-name">{item.type}</span>
                <span className="types-count">
                  {formatCount(item.total)}
                  <span className="types-percent"> ({percentage}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DuplicateData({ data }) {
  const totalValue = useSelector((state) => state.dataTagReducer.totalFiles);
  const duplicateCount = data?.duplicateFiles || 0;
  const [showDuplicateData, setShowDuplicateData] = useState(false);

  const percentage = useMemo(() => {
    if (!totalValue || totalValue === 0) return 0;
    return (
      percentageCalculation([{ count: duplicateCount }], totalValue)[0]
        ?.count || 0
    );
  }, [duplicateCount, totalValue]);

  const typesOnlyData = useMemo(() => {
    if (!data?.typeCounts) return [];

    return data.typeCounts
      .map((t) => ({
        type: t.type?.trim() || "other",
        total: t.totalCount || 0,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [data]);

  const gapAngle = 5;
  const duplicateAngle = (percentage / 100) * (360 - gapAngle * 2);
  const remainingAngle = 360 - gapAngle * 2 - duplicateAngle;

  const chartData = [
    { id: "duplicate", value: duplicateAngle, color: "#7B61FF" },
    { id: "gap1", value: gapAngle, color: "transparent" },
    { id: "remaining", value: remainingAngle, color: "#EEEAFE" },
    { id: "gap2", value: gapAngle, color: "transparent" },
  ];

  const typeData = useMemo(() => {
    if (!data?.typeCounts) return [];

    // Normalize naming + include total & duplicate
    const normalized = data.typeCounts.map((t) => ({
      type: t.type?.trim() === "" ? "other" : t.type,
      total: t.totalCount || 0,
      duplicate: t.duplicateCount || 0,
    }));

    // Sort by highest totalCount
    const sorted = normalized.sort((a, b) => b.total - a.total);

    // Take top 15 only
    const top15 = sorted.slice(0, 15);

    // Split into groups of 5
    const groups = [];
    for (let i = 0; i < top15.length; i += 5) {
      groups.push(top15.slice(i, i + 5));
    }

    return groups;
  }, [data]);

  return (
    <div
      className={
        showDuplicateData
          ? "duplicate-data-widget"
          : "duplicate-data-widget types"
      }
    >
      <div className="dd-title-row">
        <h4 className="dd-title">
          {showDuplicateData ? "Duplicate Data" : "Data Assets By Type"}
        </h4>

        <div className="dd-title toggle">
          <Switch
            size="small"
            checked={showDuplicateData}
            onChange={() => setShowDuplicateData(!showDuplicateData)}
          />
        </div>
      </div>

      {showDuplicateData ? (
        <>
          <div className="dd-chart-section duplicate">
            <Box position="relative" className="dd-chart-box">
              <PieChart
                series={[
                  {
                    data: chartData,
                    innerRadius: 50,
                    outerRadius: 65,
                    cornerRadius: 10,
                  },
                ]}
                height={140}
                width={230}
                slotProps={{ legend: { hidden: true } }}
              />

              <Box className="dd-second-chart-box">
                <Tooltip title={`Duplicate Assets: ${percentage}%`} arrow>
                  <Typography
                    variant="h5"
                    color="#1c1c28"
                    sx={{ cursor: "pointer" }}
                  >
                    {percentage}%
                  </Typography>
                </Tooltip>
                <Typography
                  variant="caption"
                  color="#9A9AAF"
                  sx={{ fontSize: "12px" }}
                />
              </Box>
            </Box>

            <div className="dd-details-section">
              <div className="dd-info">
                <div className="dd-info-row">
                  <span className="dd-line duplicate" />
                  <span className="dd-label">Duplicate Asset</span>
                  <span className="dd-value">
                    {duplicateCount.toLocaleString("en-US")}
                  </span>
                </div>

                <div className="dd-info-row">
                  <span className="dd-line total" />
                  <span className="dd-label">Total Asset</span>
                  <span className="dd-value">
                    {totalValue.toLocaleString("en-US")}
                  </span>
                </div>
              </div>
              <div>
                <div className="duplicate-spider-graph-title">By Type</div>
                <div className="dd-spiders">
                  {typeData.map((group, idx) => {
                    const label =
                      idx === 0
                        ? "Top 1–5"
                        : idx === 1
                        ? "Top 6–10"
                        : idx === 2
                        ? "Top 11–15"
                        : "";

                    return (
                      <div key={idx} className="dd-spider-item">
                        <SpiderChartSmall data={group} />
                        <div className="dd-spider-label">{label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="dd-chart-section">
          <TypesRadarWithLegend data={typesOnlyData} total={totalValue} />
        </div>
      )}
    </div>
  );
}

export default DuplicateData;
